import type { BindableQrTargetType, QrTargetType } from '@prospero/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useTranslation } from 'react-i18next';
import { QrBindingService } from '../../services/qr-binding/qr-binding.service';
import type {
  IQrBindingService,
  QrBindingResolveResult,
} from '../../services/qr-binding/qr-binding.service.interface';
import { getHttpErrorStatus } from '../../utils/error.utils';
import { useNotification } from '../use-notification';
import { qrBindingQueryKeys } from './query-keys';

export function useBindQrMutation() {
  const service = useInjection<IQrBindingService>(QrBindingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('qrBinding');

  return useMutation({
    mutationFn: (input: { qrUuid: string; targetType: BindableQrTargetType; targetId: string }) =>
      service.bind(input),
    onSuccess: (_data, _variables) => {
      // Invalidate every binding query — a rebind frees a previously-bound device, so its
      // by-target entry must refresh too.
      queryClient.invalidateQueries({ queryKey: qrBindingQueryKeys.all });
      showSuccess(t('bound'));
    },
    onError: (err: unknown) => {
      console.error('useBindQrMutation error:', err);
      const status = getHttpErrorStatus(err);
      if (status === 409) {
        showError(t('alreadyBoundElsewhere'));
        return;
      }
      if (status === 403) {
        showError(t('permissionRequired', { defaultValue: t('bindError') }));
        return;
      }
      showError(t('bindError'));
    },
  });
}

export function useUnbindQrMutation() {
  const service = useInjection<IQrBindingService>(QrBindingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('qrBinding');

  return useMutation({
    mutationFn: (input: { qrUuid: string; targetType: QrTargetType; targetId: string }) =>
      service.unbind(input.qrUuid),
    onSuccess: (_data, _variables) => {
      queryClient.invalidateQueries({ queryKey: qrBindingQueryKeys.all });
      showSuccess(t('unbound'));
    },
    onError: (err: unknown) => {
      console.error('useUnbindQrMutation error:', err);
      showError(t('unbindError'));
    },
  });
}

export function useResolveQrMutation() {
  const service = useInjection<IQrBindingService>(QrBindingService);
  const { showError } = useNotification();
  const { t } = useTranslation('qrBinding');

  return useMutation<QrBindingResolveResult | null, unknown, string>({
    mutationFn: (qrUuid: string) => service.resolve(qrUuid),
    onError: (err) => {
      console.error('useResolveQrMutation error:', err);
      showError(t('resolveError'));
    },
  });
}
